import _ from 'lodash'
import { getRequest } from '@/composables/useApi'
import { useStore } from '@/stores/user'

/** Shown once per access_token login session (survives refresh; reset on logout / new token). */
const SESSION_KEY_PREFIX = 'login-popup-shown-'

function loginSessionId() {
  if (!import.meta.client) {
    return ''
  }

  const token = localStorage.getItem('access_token')

  return token || ''
}

function sessionKey(uuid) {
  return `${SESSION_KEY_PREFIX}${loginSessionId()}:${uuid}`
}

function isConsumedThisSession(uuid) {
  if (!import.meta.client || !uuid || !loginSessionId()) {
    return false
  }

  return sessionStorage.getItem(sessionKey(uuid)) === '1'
}

export function markLoginPopupShownForSession(uuid) {
  if (!import.meta.client || !uuid || !loginSessionId()) {
    return
  }

  sessionStorage.setItem(sessionKey(uuid), '1')
}

/** Call on logout only — not on /redirect (session restore on refresh). */
export function clearLoginPopupDismissFlags() {
  if (!import.meta.client) {
    return
  }

  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith(SESSION_KEY_PREFIX)) {
      sessionStorage.removeItem(key)
    }
  })
}

function resolveUserType(userType) {
  if (userType == null) {
    return null
  }

  if (typeof userType === 'object') {
    return userType.value ?? userType.name ?? null
  }

  return userType
}

function isHrOnlyPortalUser(roles, userType) {
  const hrRoles = ['distributor_admin', 'distributor_hr']
  const hasHrRole = _.some(hrRoles, role => _.includes(roles, role))

  if (!hasHrRole) {
    return false
  }

  return userType !== 'employee'
}

export function useLoginPopupReminder() {
  const userStore = useStore()
  const message = ref(null)
  const loading = ref(false)
  const visible = ref(false)

  const isAudience = computed(() => {
    const roles = userStore.roles || []
    const userType = resolveUserType(_.get(userStore.info, 'type'))

    return !isHrOnlyPortalUser(roles, userType)
  })

  const canOpen = computed(() => {
    if (!isAudience.value || !message.value?.uuid) {
      return false
    }

    return !isConsumedThisSession(message.value.uuid)
  })

  async function fetchMessage() {
    if (!isAudience.value) {
      message.value = null
      
      return
    }

    loading.value = true

    try {
      const response = await getRequest('api/team-messages?active=1&position=login_popup&take=1')

      if (_.isString(response)) {
        message.value = null
        
        return
      }

      message.value = _.head(_.get(response, 'data.records')) || null
    } catch (err) {
      message.value = null
    } finally {
      loading.value = false
    }
  }

  function closePopup() {
    visible.value = false
  }

  watch(canOpen, value => {
    if (!value || visible.value) {
      return
    }

    markLoginPopupShownForSession(message.value.uuid)
    visible.value = true
  })

  watch(isAudience, value => {
    if (value) {
      fetchMessage()
    } else {
      message.value = null
      visible.value = false
    }
  }, { immediate: true })

  watch(
    () => [userStore.roles, userStore.info],
    () => {
      if (isAudience.value) {
        fetchMessage()
      }
    },
    { deep: true },
  )

  return {
    message,
    loading,
    visible,
    closePopup,
  }
}
